import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Grid,
  InputAdornment,
  Popover,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  CalendarToday,
  Visibility,
  VisibilityOff,
  LocalShipping as TruckIcon,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { customBillList, customBillDelete } from "../../../Services/Services";
import { printSingleCustomBill } from "./customBillPdfGenerator";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

// ── Style constants ────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// Get party name (dropdown selected OR manual text)
const getPartyName = (bill) => {
  return bill.party_name || bill.party_text || "—";
};

const getInitials = (s) =>
  s
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "";

// Generate Bill Ref No (same format as dashboard)
const getRefNo = (bill) =>
  `PRL/${bill.billing_id}/${getInitials(getPartyName(bill))}`;

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({ tip, icon, isDelete, isEdit, isPrint, onClick }) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isPrint
        ? T.success
        : T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isPrint
        ? T.successBg
        : T.primaryBg;

  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isPrint
        ? T.successBorder
        : T.border;

  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isPrint
        ? "#d1fae5"
        : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 22,
          height: 22,
          ...sq,
          color: c,
          bgcolor: bg,
          border: `1px solid ${bd}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            borderColor: c,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 13 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── DeletePopover ──────────────────────────────────────────────────
const DeletePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 260 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.redBg,
            border: `1.5px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.8rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Delete Custom Bill?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.68rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        This action cannot be undone.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── MobileCustomBillCard ───────────────────────────────────────────
const MobileCustomBillCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onPrint,
}) => {
  const partyName = getPartyName(row);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <ReceiptIcon
            sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 0.25 }}
          >
            <Chip
              label={getRefNo(row)}
              size="small"
              sx={{
                ...sq,
                bgcolor: "#FEE2E2",
                color: "#DC2626",
                border: "1px solid #FECACA",
                fontSize: "0.55rem",
                height: 16,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                color: T.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.invoice_no || `INV/${row.billing_id}`}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
              textAlign: "left",
            }}
          >
            {partyName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.6rem", color: T.textMuted }}>
              {formatDate(row.invoice_date || row.created_at)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Grand Total
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.8rem", color: T.primary }}
            >
              {formatCurrency(row.grand_total)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1}>
            {/* Party Details */}
            <Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.55rem",
                    color: T.textMuted,
                    fontWeight: 600,
                  }}
                >
                  Party Name
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                  {partyName}
                </Typography>
              </Box>
            </Box>

            {/* Amount Details */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryBg,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.75,
                  textTransform: "uppercase",
                }}
              >
                💰 Amount Summary
              </Typography>
              <Grid container spacing={0.75}>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.55rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Taxable
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 700, color: T.text }}
                  >
                    {formatCurrency(row.total_taxable_value)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.55rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    CGST
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#92400e",
                    }}
                  >
                    {formatCurrency(row.cgst_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.55rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    SGST
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#92400e",
                    }}
                  >
                    {formatCurrency(row.sgst_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 0.5,
                      pt: 0.5,
                      borderTop: `1px dashed ${T.border}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: T.primary,
                      }}
                    >
                      GRAND TOTAL
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 900,
                        color: T.primary,
                      }}
                    >
                      {formatCurrency(row.grand_total)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Other Info */}
            {(row.eway_bill_no || row.motor_vehicle_no) && (
              <Stack spacing={0.5}>
                {row.eway_bill_no && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: "0.65rem", color: T.textSub }}>
                      E-way Bill:
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                      {row.eway_bill_no}
                    </Typography>
                  </Box>
                )}
                {row.motor_vehicle_no && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: "0.65rem", color: T.textSub }}>
                      Vehicle No:
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                      {row.motor_vehicle_no}
                    </Typography>
                  </Box>
                )}
                {row.destination && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ fontSize: "0.65rem", color: T.textSub }}>
                      Destination:
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                      {row.destination}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{
                ...sq,
                bgcolor: T.successBg,
                color: T.success,
                border: `1.5px solid ${T.successBorder}`,
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1.5,
                height: 30,
                "&:hover": { bgcolor: "#d1fae5", borderColor: T.success },
              }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, row.billing_id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ── Main Component ─────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export default function CustomBillList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filters
  const [filters, setFilters] = useState({
    invoice_no: "",
    billDate: "",
    party: "",
  });

  const debouncedInvoice = useDebounce(filters.invoice_no, 500);
  const debouncedDate = useDebounce(filters.billDate, 500);
  const debouncedParty = useDebounce(filters.party, 500);

  const handleFilterChange = (key, value) =>
    setFilters((p) => ({ ...p, [key]: value }));

  const clearFilters = () =>
    setFilters({ invoice_no: "", billDate: "", party: "" });

  const hasActiveFilters =
    filters.invoice_no || filters.billDate || filters.party;

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  // ── Fetch data ──
  const fetchBills = useCallback(async (pageNo = 1, appliedFilters = {}) => {
    setLoading(true);
    try {
      const res = await customBillList(pageNo, appliedFilters);
      const bills = Array.isArray(res.results) ? res.results : [];
      if (pageNo === 1) setData(bills);
      else setData((p) => [...p, ...bills]);
      setHasMore(!!res.next);
      setPage(pageNo + 1);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to fetch custom bills",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const appliedFilters = {
      invoice_no: debouncedInvoice,
      billDate: debouncedDate,
      party: debouncedParty,
    };
    setData([]);
    setPage(1);
    setExpandedCardId(null);
    fetchBills(1, appliedFilters);
  }, [debouncedInvoice, debouncedDate, debouncedParty]);

  const fetchMoreData = () => {
    if (loading || !hasMore) return;
    fetchBills(page, {
      invoice_no: debouncedInvoice,
      billDate: debouncedDate,
      party: debouncedParty,
    });
  };

  // ── Handlers ──
  const handleEdit = (row) => {
    navigate(`/dashboard/custom-bill/edit/${row.billing_id}`, {
      state: { editData: row },
    });
  };

  const handlePrint = (row) => {
    printSingleCustomBill(row);
  };

  const handleDelete = async (id) => {
    // Close popover first for better UX
    setDeleteAnchorEl(null);
    setDeleteId(null);

    try {
      await customBillDelete(id);
      setData((p) => p.filter((r) => r.billing_id !== id));
      setSnackbar({
        open: true,
        message: "Custom bill deleted successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Delete error:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || "Failed to delete custom bill",
        severity: "error",
      });
    }
  };

  // ── Totals ──
  const totals = useMemo(
    () => ({
      taxable: data.reduce((s, r) => s + Number(r.total_taxable_value || 0), 0),
      cgst: data.reduce((s, r) => s + Number(r.cgst_amount || 0), 0),
      sgst: data.reduce((s, r) => s + Number(r.sgst_amount || 0), 0),
      grand: data.reduce((s, r) => s + Number(r.grand_total || 0), 0),
    }),
    [data],
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 0 },
        pt: 0,
        mt: { xs: 1, md: -3 },
        maxWidth: 1400,
        mx: "auto",
        width: "100%",
      }}
    >
      {/* ── Stats ── */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        {[
          {
            title: "Total Taxable",
            value: formatCurrency(totals.taxable),
            color: T.primary,
          },
          {
            title: "Total CGST",
            value: formatCurrency(totals.cgst),
            color: "#DC2626",
          },
          {
            title: "Total SGST",
            value: formatCurrency(totals.sgst),
            color: "#EA580C",
          },
          {
            title: "Grand Total",
            value: formatCurrency(totals.grand),
            color: T.success,
          },
        ].map(({ title, value, color }) => (
          <Grid item xs={6} sm={3} key={title}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                ...sq,
                bgcolor: `${color}12`,
                border: `1px solid ${color}40`,
                borderTop: `3px solid ${color}`,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.65rem" },
                  color: T.textSub,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  mb: 0.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  fontWeight: 800,
                  color,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              size="small"
              placeholder="Invoice No..."
              label="Invoice No"
              fullWidth
              value={filters.invoice_no}
              onChange={(e) => handleFilterChange("invoice_no", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              type="date"
              size="small"
              label="Invoice Date"
              fullWidth
              value={filters.billDate}
              onChange={(e) => handleFilterChange("billDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputProps: { max: new Date().toISOString().split("T")[0] },
              }}
              sx={filterInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              placeholder="Party Name"
              label="Party"
              fullWidth
              value={filters.party}
              onChange={(e) => handleFilterChange("party", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {hasActiveFilters && (
            <Grid item xs="auto">
              <Tooltip title="Clear Filters" arrow>
                <Button
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
            </Grid>
          )}

          <Grid item xs="auto">
            <Tooltip title="Refresh" arrow>
              <Button
                onClick={() => {
                  setPage(1);
                  fetchBills(1, {
                    invoice_no: debouncedInvoice,
                    billDate: debouncedDate,
                    party: debouncedParty,
                  });
                }}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <RefreshIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs={12}
            sm="auto"
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          <Grid item xs={12} sm="auto">
            <Box
              sx={{
                height: 34,
                px: 1.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                border: `1.5px solid ${T.border}`,
              }}
            >
              <ReceiptIcon sx={{ fontSize: 15, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                }}
              >
                {data.length} Bills
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Mobile Cards ── */}
      {isMobile ? (
        loading && data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}
            >
              No custom bills found
            </Typography>
          </Box>
        ) : (
          <Box
            id="custom-bill-mobile"
            sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="custom-bill-mobile"
            >
              {data.map((row, idx) => (
                <MobileCustomBillCard
                  key={row.billing_id}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === row.billing_id}
                  onToggle={() => handleCardToggle(row.billing_id)}
                  onEdit={handleEdit}
                  onDelete={(e, id) => {
                    setDeleteAnchorEl(e.currentTarget);
                    setDeleteId(id);
                  }}
                  onPrint={handlePrint}
                />
              ))}
            </InfiniteScroll>
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 240px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="custom-bill-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="custom-bill-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1740,
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 1,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                  "& td": { height: 28 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "#", sticky: true, left: 0, w: 40 },
                      { l: "Bill ID", sticky: true, left: 40, w: 140 },
                      { l: "Invoice No", sticky: true, left: 180, w: 120 },
                      { l: "Invoice Date", sticky: true, left: 300, w: 100 },
                      { l: "Party", w: 200 },
                      { l: "E-way Bill", w: 120 },
                      { l: "Vehicle No", w: 100 },
                      { l: "Destination", w: 120 },
                      { l: "Taxable", w: 110 },
                      { l: "CGST", w: 100 },
                      { l: "SGST", w: 100 },
                      { l: "Grand Total", w: 120 },
                      { l: "Actions", sticky: true, right: 0, w: 130 },
                    ].map(({ l, sticky, left, right, w }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            ...(left !== undefined && { left }),
                            ...(right !== undefined && { right }),
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <ReceiptIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No custom bills found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;
                      const stickyCell = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };

                      return (
                        <TableRow
                          key={row.billing_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* # */}
                          <TableCell
                            sx={{ position: "sticky", left: 0, ...stickyCell }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* Bill ID (Ref No) — NEW */}
                          <TableCell
                            sx={{ position: "sticky", left: 40, ...stickyCell }}
                          >
                            <Chip
                              label={getRefNo(row)}
                              size="small"
                              sx={{
                                ...sq,
                                bgcolor: "#FEE2E2",
                                color: "#DC2626",
                                border: "1px solid #FECACA",
                                fontSize: "0.62rem",
                                height: 18,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Invoice No */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 180,
                              ...stickyCell,
                            }} // ← left shifted
                          >
                            <Chip
                              label={row.invoice_no || `INV/${row.billing_id}`}
                              size="small"
                              sx={{
                                ...sq,
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                border: `1px solid ${T.primary}40`,
                                fontSize: "0.62rem",
                                height: 18,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Invoice Date */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 300, // ← left shifted
                              ...stickyCell,
                            }}
                          >
                            <Chip
                              label={formatDate(
                                row.invoice_date || row.created_at,
                              )}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.white,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Tooltip title={getPartyName(row)} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getPartyName(row)}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* E-way Bill */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontFamily: "monospace",
                                color: T.text,
                              }}
                            >
                              {row.eway_bill_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* Vehicle No */}
                          <TableCell>
                            {row.motor_vehicle_no ? (
                              <Chip
                                label={row.motor_vehicle_no}
                                size="small"
                                icon={
                                  <TruckIcon
                                    sx={{ fontSize: "10px !important" }}
                                  />
                                }
                                sx={{
                                  ...sq,
                                  height: 18,
                                  bgcolor: "#fef3c7",
                                  color: "#92400e",
                                  border: "1px solid #fcd34d",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.5 },
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.textMuted }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Destination */}
                          <TableCell>
                            <Tooltip title={row.destination || "—"} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.destination || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Taxable */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {formatCurrency(row.total_taxable_value)}
                            </Typography>
                          </TableCell>

                          {/* CGST */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#DC2626",
                              }}
                            >
                              {formatCurrency(row.cgst_amount)}
                              {row.cgst_percent > 0 && (
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "0.55rem",
                                    color: T.textMuted,
                                    ml: 0.5,
                                  }}
                                >
                                  ({row.cgst_percent}%)
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>

                          {/* SGST */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#EA580C",
                              }}
                            >
                              {formatCurrency(row.sgst_amount)}
                              {row.sgst_percent > 0 && (
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "0.55rem",
                                    color: T.textMuted,
                                    ml: 0.5,
                                  }}
                                >
                                  ({row.sgst_percent}%)
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>

                          {/* Grand Total */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 800,
                                color: T.primary,
                              }}
                            >
                              {formatCurrency(row.grand_total)}
                            </Typography>
                          </TableCell>

                          {/* Actions - Sticky Right */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...stickyCell,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEdit(row)}
                              />
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                isPrint
                                onClick={() => handlePrint(row)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={(e) => {
                                  setDeleteAnchorEl(e.currentTarget);
                                  setDeleteId(row.billing_id);
                                }}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}

                  {/* Totals Row */}
                  {data.length > 0 && (
                    <TableRow
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 20,
                        "& td": {
                          bgcolor: `${T.successBg} !important`,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          py: 1,
                          borderTop: `2px solid ${T.primary}`,
                          borderBottom: "none",
                        },
                      }}
                    >
                      {/* TOTALS spans first 3 sticky cols (#, Invoice No, Invoice Date) */}
                      <TableCell
                        colSpan={4}
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 22,
                          bgcolor: `${T.successBg} !important`,
                          color: T.primary,
                          fontWeight: 800,
                          fontSize: "0.75rem !important",
                        }}
                      >
                        TOTALS
                      </TableCell>

                      {/* 4 empty cells: Party, E-way, Vehicle, Destination */}
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell />

                      {/* Taxable */}
                      <TableCell
                        align="right"
                        sx={{ color: T.primary, fontWeight: 700 }}
                      >
                        {formatCurrency(totals.taxable)}
                      </TableCell>

                      {/* CGST */}
                      <TableCell
                        align="right"
                        sx={{ color: "#DC2626", fontWeight: 700 }}
                      >
                        {formatCurrency(totals.cgst)}
                      </TableCell>

                      {/* SGST */}
                      <TableCell
                        align="right"
                        sx={{ color: "#EA580C", fontWeight: 700 }}
                      >
                        {formatCurrency(totals.sgst)}
                      </TableCell>

                      {/* Grand Total */}
                      <TableCell
                        align="right"
                        sx={{ color: T.primary, fontWeight: 800 }}
                      >
                        {formatCurrency(totals.grand)}
                      </TableCell>

                      {/* Actions - sticky right */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 22,
                          bgcolor: `${T.successBg} !important`,
                        }}
                      />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Delete Popover ── */}
      <DeletePopover
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        onConfirm={() => handleDelete(deleteId)}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            ...sq,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontSize: "0.8rem",
          }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
